/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
import { ApolloProvider, useQuery } from '@apollo/client';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

/** css can be imported from antd only for a single component */
// import 'antd/es/button/style/index.css';

/** custom import's */
import { GenericTemplate, PageTemplate } from 'components/templates';
import { detect, fromNavigator, fromStorage } from '@lingui/detect-locale';
import { IS_LOGGED_IN } from 'graphql/queries';
import { client } from 'utils/apollo-client';
import { defaultLocale, dynamicActivate } from 'utils/i18n';

const AppRoutes = lazy(() =>
  import('routes/AppRoutes').then((mod) => ({ default: mod.AppRoutes })),
);
const NotAuthAppRoutes = lazy(() =>
  import('routes').then((mod) => ({ default: mod.NotAuthAppRoutes })),
);

const DEFAULT_FALLBACK = () => defaultLocale;

export const preferredDefaultLanguage = detect(
  fromStorage('lang'),
  fromNavigator(),
  DEFAULT_FALLBACK,
);

const IsLoggedIn = () => {
  const { data } = useQuery(IS_LOGGED_IN);
  return (
    <Router>
      <Suspense fallback={null}>
        {data.isLoggedIn ? (
          <PageTemplate>
            <AppRoutes />
          </PageTemplate>
        ) : (
          <GenericTemplate>
            <NotAuthAppRoutes />
          </GenericTemplate>
        )}
      </Suspense>
    </Router>
  );
};

export function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const initial = preferredDefaultLanguage?.split('-')[0] || defaultLocale;
    dynamicActivate(initial).finally(() => setReady(true));
  }, []);

  if (!ready) return null; // or a splash

  return (
    <ApolloProvider client={client}>
      <I18nProvider i18n={i18n}>
        <IsLoggedIn />
      </I18nProvider>
    </ApolloProvider>
  );
}
